import { Dispatch, useState } from "react";

// Interfaces/Types
import { TValue } from "@/Settings/types/SystemSettings/appendingConfiguration";

// Helpers
import RowFeedback from "@/Common/helpers/RowFeedback";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// MUI
import { useGridApiContext, GridRowModes, GridRowModesModel } from "@mui/x-data-grid-premium";

const resolveError = ({ isAccountDescription, oem, value }: { isAccountDescription: boolean; oem: string; value: unknown }) => {
	if (isAccountDescription) {
		return !value;
	} else {
		return oem && !value;
	}
};

const resolveDisabled = ({ isAccountDescription, oem }: { isAccountDescription: boolean; oem: string }) => {
	if (isAccountDescription) {
		return false;
	} else {
		return !oem;
	}
};

type TInputSelectCellProps = {
	id: string;
	field: string;
	tooltipText?: string;
	setRowModesModel: (value: React.SetStateAction<GridRowModesModel>) => void;
	disabled?: boolean;
	hideGroups: boolean;
	placeholder: string;
	list?: TValue[];
	oem: string;
	setOem: Dispatch<React.SetStateAction<string>>;
};

const InputSelectCell = (props: TInputSelectCellProps) => {
	const { id, field, setRowModesModel, hideGroups, placeholder, list = [], oem, setOem } = props;
	const apiContextRef = useGridApiContext();
	const [value, setValue] = useState<TValue>();

	const isAccountDescription = field === "accountDescription";
	const isGlAccount = field === "glAccount";
	const isDisabled = resolveDisabled({ isAccountDescription, oem });
	const isError = resolveError({ isAccountDescription, oem, value });

	const handleSaveClick = (saveId: string, saveField: string) => {
		const rowEdit = apiContextRef.current.state.editRows;
		const row = rowEdit[saveId];
		const keysToCheckErrors = Object.keys(row).filter((key) => key !== saveField);
		const existErrors = keysToCheckErrors.some((key) => {
			if (key === "info") {
				return false;
			}
			if (hideGroups && key === "groupInfo") {
				return false;
			}
			return !row[key]?.value?.label;
		});
		if (isGlAccount) {
			setOem("");
		}
		if (!existErrors) {
			setRowModesModel((prev) => {
				return {
					...prev,
					[saveId]: { mode: GridRowModes.View },
				};
			});
			RowFeedback?.skeleton(saveId, apiContextRef)?.display();
		}
	};

	const handleChange = (newValue: TValue) => {
		if (newValue) {
			apiContextRef.current.setEditCellValue({ id, field, value: newValue, debounceMs: 200 });
			setValue(newValue);
		}
		if (newValue && isAccountDescription) {
			setOem(newValue?.label);
		}
		handleSaveClick(id, field);
	};

	return (
		<SmartSelector
			selectBy={"label"}
			options={list || []}
			selected={value}
			inputProps={{
				placeholder,
				classes:
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] [&_.MuiInputBase-root_.MuiInputBase-input]:!rounded-none",
			}}
			listBoxProps={{
				sx: { maxHeight: "223px" },
			}}
			popperProps={{
				sx: { minWidth: "180px", maxWidth: "250px" },
			}}
			disabled={isDisabled}
			error={Boolean(isError)}
			onChangeAction={(selected) => handleChange(selected as TValue)}
		/>
	);
};

export default InputSelectCell;
